/* eslint-disable vars-on-top */
/* eslint-disable prefer-template */

'use strict';

import { CONST } from 'app_vodafone_ziggo/util/util';
import * as debounce from 'lodash/debounce';

/**
 * @class VZLayoutToggle
 * @description
 * Creates a VZLayoutToggle component
 * ---
 * 'data-layout-key' : local storage entry ID |
 * 'data-layout-id-default' : default choice ID |
 * 'data-layout-container' : parent container identifier |
 * 'data-layout-enabled-breakpoint' : breakpoint from which enabled |
 * 'data-layout-id' : layout IDs ('agenda', 'grid', 'other') |
 * 'data-layout-class' : CSS classes to add to parent container
 *
 * @example
 * <vz-layout-toggle
 *      data-layout-key="plpPreferredLayoutId"
 *      data-layout-id-default="agenda"
 *      data-layout-container="js-product-listing"
 *      data-layout-enabled-breakpoint="1200"
 *      data-layout-change-message="View changed to"
 * >
 *      <div class="layout-toggle-container" role="group" aria-label="Layout options" aria-describedby="layout-toggle-status">
 *          <button
 *              type="button"
 *              class="layout-toggle js-layout-toggle"
 *              data-layout-id="grid"
 *              data-layout-class="product-listing--grid"
 *              data-layout-text="grid view"
 *              aria-label="Grid view"
 *              aria-pressed="false"
 *          >
 *              <i class="icon icon-view-grid" aria-hidden="true"></i>
 *          </button>
 *          <button
 *              type="button"
 *              class="layout-toggle js-layout-toggle"
 *              data-layout-id="agenda"
 *              data-layout-class="product-listing--agenda"
 *              data-layout-text="list view"
 *              aria-label="List view"
 *              aria-pressed="false"
 *          >
 *              <i class="icon icon-view-agenda" aria-hidden="true"></i>
 *          </button>
 *      </div>
 *
 *      <div id="layout-toggle-status" class="visually-hidden" aria-live="polite" aria-atomic="true">
 *          <span class="js-layout-status-text"></span>
 *      </div>
 * </vz-layout-toggle>
 */
class VZLayoutToggle extends HTMLElement {
    constructor() {
        super();

        // Classes
        this.activeClass = 'layout-toggle--active';

        // Configurables
        this.dataLayoutLocalStorageKey = this.getAttribute(CONST.vzLayoutToggle.localStorageKey) || CONST.localStorage.preferredLayoutId;
        this.dataLayoutIdDefault = this.getAttribute(CONST.vzLayoutToggle.idDefault) || 'agenda';
        this.dataLayoutContainer = this.getAttribute(CONST.vzLayoutToggle.container) || null;
        this.enabledOnBreakpoint = parseInt(this.getAttribute(CONST.vzLayoutToggle.enabledBreakpoint) || CONST.breakpoints.lg, 10);
        this.dataLayoutChangeMessage = this.getAttribute('data-layout-change-message');

        // Toggles and layout container
        this.toggles = this.querySelectorAll('.js-layout-toggle');
        this.layoutContainer = document.querySelector(`.${this.dataLayoutContainer}`);
        this.statusTextElement = this.querySelector('.js-layout-status-text');
    }

    /**
     * Fetches the previously stored layout choice from local storage using the local storage key for this instance of VZLayoutToggle.
     * If the entry does not exist sets a default value.
     * @function fetch
     * @returns {void}
     */
    fetch() {
        this.activeLayoutId = window.localStorage.getItem(this.dataLayoutLocalStorageKey) || this.dataLayoutIdDefault;
    }

    /**
     * Checks if the previously stored layout choice is valid for this instance of VZLayoutToggle.
     * If not, resets it to the default value.
     * @function setup
     * @returns {void}
     */
    setup() {
        var activeToggleExists = this.querySelector(`[${CONST.vzLayoutToggle.id}=${this.activeLayoutId}]`) || null;

        if (!activeToggleExists) this.activeLayoutId = this.dataLayoutIdDefault;
    }

    /**
     * Triggers an update to toggleLayout with the initial layout choice.
     * @function enable
     * @returns {void}
     */
    enable() {
        this.activeToggle = this.querySelector(`[${CONST.vzLayoutToggle.id}=${this.activeLayoutId}]`);
        this.activeLayoutClass = this.activeToggle.getAttribute(CONST.vzLayoutToggle.class) || 'active';

        this.toggleLayout(this.activeToggle);
    }

    /**
     * Triggers an update to setPreferredLayout and toggleClasses with the new layout choice.
     * @function toggleLayout
     * @param {Event} e - The click event.
     * @returns {void}
     */
    toggleLayout(e) {
        var toggle = e.currentTarget || e;
        var layoutId = toggle.getAttribute(CONST.vzLayoutToggle.id);
        var layoutClass = toggle.getAttribute(CONST.vzLayoutToggle.class);

        this.setPreferredLayout(layoutId, layoutClass);
        this.toggleClasses(toggle);
    }

    /**
     * Updates activeLayoutId, activeLayoutClass and local storage with the new layout choice.
     * @function setPreferredLayout
     * @param {string} layoutId - The chosen layout identifier.
     * @param {string} layoutClass - The chosen layout identifier class.
     * @returns {void}
     */
    setPreferredLayout(layoutId, layoutClass) {
        this.activeLayoutId = layoutId;
        this.activeLayoutClass = layoutClass;

        window.localStorage.setItem(this.dataLayoutLocalStorageKey, this.activeLayoutId);
    }

    /**
     * Toggles the highlight classes for the toggles and layout container element.
     * @function toggleClasses
     * @param {HTMLElement} selectedToggle - The chosen layout toggle.
     * @returns {void}
     */
    toggleClasses(selectedToggle) {
        this.toggles.forEach((toggle) => {
            var layoutClass = toggle.getAttribute(CONST.vzLayoutToggle.class);
            this.layoutContainer.classList.remove(layoutClass);

            toggle.classList.remove(this.activeClass);
            toggle.setAttribute('aria-pressed', 'false');
        });

        selectedToggle.classList.add(this.activeClass);
        selectedToggle.setAttribute('aria-pressed', 'true');

        this.layoutContainer.classList.add(this.activeLayoutClass);

        // Update aria-live region for screen readers
        if (this.statusTextElement) {
            const layoutText = selectedToggle.getAttribute('data-layout-text');
            this.statusTextElement.textContent = `${this.dataLayoutChangeMessage} ${layoutText}`;
        }
    }

    /**
     * Shows VZLayoutToggle.
     * @function show
     * @returns {void}
     */
    show() {
        this.classList.remove('d-none');
    }

    /**
     * Hides VZLayoutToggle.
     * @function hide
     * @returns {void}
     */
    hide() {
        this.classList.add('d-none');
    }

    /**
     * Handles VZLayoutToggle visibility on window resize.
     * @function handleResize
     * @returns {void}
     */
    handleResize() {
        if (window.innerWidth < this.enabledOnBreakpoint) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * @function connectedCallback
     * @description Initializes the component and sets up event listeners.
     * @returns {void}
     */
    connectedCallback() {
        // There is no valid layout container specified
        if (!this.dataLayoutContainer || !this.layoutContainer) return;

        this.fetch();
        this.setup();
        this.enable();

        // Make visible if enabled for current breakpoint
        if (window.innerWidth >= this.enabledOnBreakpoint) this.show();

        // Events
        this.toggles.forEach((toggle) => {
            toggle.addEventListener('click', this.toggleLayout.bind(this));
        });

        window.addEventListener(
            'resize',
            debounce(() => {
                this.handleResize();
            }),
            150
        );
    }
}

window.VZLayoutToggle = window.customElements.get('vz-layout-toggle') || VZLayoutToggle;
export default window.VZLayoutToggle;

if (!window.customElements.get('vz-layout-toggle')) {
    window.customElements.define('vz-layout-toggle', VZLayoutToggle);
}
